// Single source of truth for the PWHL standings column catalogue.
// Admins choose which columns appear AND their order (PWHLStandingsAdmin);
// the public standings table (Teams.jsx) renders exactly that config.

export const STANDINGS_COLS = [
    { key: "gp", label: "GP", title: "Games Played" },
    { key: "gr", label: "GR", title: "Games Remaining" },
    { key: "mag", label: "MAG", title: "Magic Number — points needed to clinch a playoff berth (x = clinched, e = eliminated)" },
    { key: "gold_pts", label: "GOLD", title: "Gold Plan points — points earned after elimination from playoff contention (shows once at least one team is eliminated)" },
    { key: "record", label: "W-OTW-OTL-L", title: "Record (Wins-OT Wins-OT Losses-Losses)" },
    { key: "shootout", label: "SOW-SOL", title: "Shootout (Wins-Losses)" },
    { key: "ptspct", label: "PTS%", title: "Points Percentage" },
    { key: "streak", label: "STREAK", title: "Current Streak" },
    { key: "l10", label: "L10", title: "Last 10 (W-OTW-OTL-L)" },
    { key: "home_rec", label: "HOME", title: "Home Record (W-OTW-OTL-L)" },
    { key: "away_rec", label: "AWAY", title: "Away Record (W-OTW-OTL-L)" },
    { key: "gfga", label: "GF-GA", title: "Goals For - Goals Against" },
    { key: "gf_gp", label: "GF/GP", title: "Goals For per Game" },
    { key: "ga_gp", label: "GA/GP", title: "Goals Against per Game" },
    { key: "gd", label: "GD", title: "Goal Differential" },
    { key: "pts", label: "PTS", title: "Points" },
    { key: "w", label: "W", title: "Regulation Wins" },
    { key: "otw", label: "OTW", title: "Overtime Wins" },
    { key: "otl", label: "OTL", title: "Overtime Losses" },
    { key: "l", label: "L", title: "Regulation Losses" },
    { key: "gf", label: "GF", title: "Goals For" },
    { key: "ga", label: "GA", title: "Goals Against" },
    { key: "sow", label: "SOW", title: "Shootout Wins" },
    { key: "sol", label: "SOL", title: "Shootout Losses" },
    { key: "pp", label: "PP%", title: "Power Play %" },
    { key: "pk", label: "PK%", title: "Penalty Kill %" },
    { key: "ppop", label: "PPOP", title: "Power Play Opportunities" },
    { key: "ppgf", label: "PPGF", title: "Power Play Goals For" },
    { key: "tsh", label: "TSH", title: "Times Short-Handed" },
    { key: "ppga", label: "PPGA", title: "Power Play Goals Against" },
    { key: "shgf", label: "SHGF", title: "Short-Handed Goals For" },
    { key: "shga", label: "SHGA", title: "Short-Handed Goals Against" },
    { key: "esgf", label: "ESGF", title: "Even-Strength Goals For" },
    { key: "esga", label: "ESGA", title: "Even-Strength Goals Against" },
    { key: "engf", label: "ENGF", title: "Empty-Net Goals For" },
    { key: "enga", label: "ENGA", title: "Empty-Net Goals Against" },
    { key: "ps", label: "PS", title: "Penalty-Shot Goals / Attempts" },
    { key: "fg", label: "FG", title: "First goals of the game" },
    { key: "lg", label: "LG", title: "Last goals of the game" },
    { key: "shots", label: "SHOTS", title: "Shots" },
    { key: "shpct", label: "SH%", title: "Shooting % (Goals For ÷ Shots)" },
    { key: "fo", label: "FO", title: "Faceoffs Won" },
    { key: "fopct", label: "FO%", title: "Faceoff Win %" },
    { key: "hits", label: "HITS", title: "Hits" },
    { key: "blk", label: "BLK", title: "Blocked Shots" },
    { key: "pim", label: "PIM", title: "Penalty Minutes" },
    { key: "no", label: "NO", title: "Next Opponent" },
];

export const DEFAULT_STANDINGS_COLS = ["gp", "gr", "record", "shootout", "ptspct", "streak", "l10", "home_rec", "away_rec", "gfga", "gd", "pp", "pk"];

// Two fixed public presets (Basic | Advanced). Rank lives in the team cell; NO is always appended.
export const BASIC_STANDINGS_COLS = ["gp", "pts", "record", "gfga", "gd", "pp", "pk", "streak"];
export const ADVANCED_STANDINGS_COLS = ["gr", "ptspct", "shootout", "gf_gp", "ga_gp", "gd", "home_rec", "away_rec", "l10"];

// Basic | Advanced are ALTERNATE VIEWS of the SAME fixed column slots. Toggling
// only swaps each cell's content (and header label) in place; the column count,
// order and width stay constant so the table never reflows between views.
export const STANDINGS_VIEW_PAIRS = [
    { basic: { key: "gp", label: "GP", title: "Games Played" }, advanced: { key: "gr", label: "GR", title: "Games Remaining" } },
    { basic: { key: "pts", label: "PTS", title: "Points" }, advanced: { key: "ptspct", label: "PTS%", title: "Points Percentage" } },
    { basic: { key: "record", label: "W-OTW-OTL-L", title: "Record" }, advanced: { key: "record_so", label: "W-SOW-SOL-L", title: "Record (Wins-SO Wins-SO Losses-Losses)" } },
    { basic: { key: "gfga", label: "GF-GA", title: "Goals For - Goals Against" }, advanced: { key: "gpg", label: "GF/GP·GA/GP", title: "Goals For / Against per Game" } },
    { basic: { key: "gd", label: "GD", title: "Goal Differential" }, advanced: { key: "gd", label: "GD", title: "Goal Differential" } },
    { basic: { key: "pppk", label: "PP%·PK%", title: "Power Play % · Penalty Kill %" }, advanced: { key: "st_adv", label: "PPGF/OP·PPGA/SH", title: "PP Goals/Opportunities · PP Goals Against/Times Short" } },
    { basic: { key: "streak", label: "STREAK", title: "Current Streak" }, advanced: { key: "l10", label: "L10", title: "Last 10 (W-OTW-OTL-L)" } },
];

export const STANDINGS_COL_LABEL = Object.fromEntries(STANDINGS_COLS.map((c) => [c.key, c.label]));

// A column entry may carry a per-instance suffix ("gf#1729…") when duplicated in
// a view so each copy can have its own name/width/bold. baseKey() strips it back
// to the catalogue key used for rendering/validation/sorting.
export const baseKey = (k) => String(k).split("#")[0];

// Resolve a stored config (array of keys) into a clean, valid ordered list.
// Falls back to the default order when the stored value is empty/invalid.
export function resolveStandingsCols(stored) {
    const valid = new Set(STANDINGS_COLS.map((c) => c.key));
    const out = (Array.isArray(stored) ? stored : []).filter((k) => valid.has(k));
    if (!out.length) return DEFAULT_STANDINGS_COLS;
    // Soft-migrate pre-redesign configs (which predate the grouped Record/Shootout/GR
    // columns) to the new default look. Once an admin re-saves via the column picker,
    // the new keys are present and the stored order is respected verbatim.
    const redesignKeys = ["gr", "record", "shootout"];
    if (!out.some((k) => redesignKeys.includes(k))) return DEFAULT_STANDINGS_COLS;
    return out;
}
